'use client';

import { c as _c } from "react/compiler-runtime";
import { LinkComponent } from '@/components/ui/link-component';
import { HighlightedText } from '@/features/payload-cms/components/search/highlighted-text';
import { resultMetaParts, resultTypeIcons } from '@/features/payload-cms/components/search/search-result-meta';
import { moreMatchesLabel, pageShortLabel } from '@/features/payload-cms/components/search/search-translations';
import type { SearchResult } from '@/features/payload-cms/search/search-types';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import type React from 'react';
import { useState } from 'react';

/** Below `xl` there is no preview, so a document row lists its first Fundstellen itself. */
const INLINE_MATCH_COUNT = 2;

/** The page of a PDF to open: its viewer jumps there from the fragment. */
export const fileUrlOnPage = (fileUrl: string, page: number): string => `${fileUrl}#page=${page}`;
const hasInlineMatches = (result: SearchResult): boolean => (result.document?.matches.length ?? 0) > 0;
const ResultIcon: React.FC<{
  result: SearchResult;
  isSelected: boolean;
}> = t0 => {
  const $ = _c(9);
  const {
    result,
    isSelected
  } = t0;
  const thumbnail = result.document?.thumbnailUrl;
  if (thumbnail !== undefined) {
    let t1;
    if ($[0] !== thumbnail) {
      t1 = <img src={thumbnail} alt="" loading="lazy" className="border-line h-[54px] w-10 shrink-0 rounded-md border bg-white object-cover object-top" />;
      $[0] = thumbnail;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const Icon = resultTypeIcons[result.type];
  const t1 = isSelected ? "text-conveniat-green bg-white" : "bg-tint text-conveniat-green";
  let t2;
  if ($[2] !== t1) {
    t2 = cn("flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px]", t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== Icon) {
    t3 = <Icon className="h-5 w-5" aria-hidden="true" />;
    $[4] = Icon;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
const RowBody: React.FC<{
  result: SearchResult;
  words: string[];
  locale: Locale;
  isSelected: boolean;
}> = t0 => {
  const $ = _c(21);
  const {
    result,
    words,
    locale,
    isSelected
  } = t0;
  let t1;
  if ($[0] !== isSelected || $[1] !== result) {
    t1 = <ResultIcon result={result} isSelected={isSelected} />;
    $[0] = isSelected;
    $[1] = result;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== result.title || $[4] !== words) {
    t2 = <span className="font-heading text-heading text-[16px] leading-snug font-bold"><HighlightedText text={result.title} words={words} /></span>;
    $[3] = result.title;
    $[4] = words;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== locale || $[7] !== result) {
    t3 = resultMetaParts(result, locale).join(" \xB7 ");
    $[6] = locale;
    $[7] = result;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <span className="mt-0.5 truncate text-[13px] text-green-500">{t3}</span>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== result || $[12] !== words) {
    t5 = result.excerpt !== "" && <span className={cn("mt-1 line-clamp-2 text-[14px] leading-normal text-green-700", hasInlineMatches(result) && "max-xl:hidden")}><HighlightedText text={result.excerpt} words={words} /></span>;
    $[11] = result;
    $[12] = words;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t2 || $[15] !== t4 || $[16] !== t5) {
    t6 = <span className="flex min-w-0 flex-1 flex-col">{t2}{t4}{t5}</span>;
    $[14] = t2;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t1 || $[19] !== t6) {
    t7 = <>{t1}{t6}</>;
    $[18] = t1;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};

/** The first Fundstellen of a document, each a link to its page, where no preview shows them. */
const InlineMatches: React.FC<{
  result: SearchResult;
  words: string[];
  locale: Locale;
}> = t0 => {
  const $ = _c(20);
  const {
    result,
    words,
    locale
  } = t0;
  const details = result.document;
  if (details === undefined || !hasInlineMatches(result)) {
    return;
  }
  let remaining;
  let t1;
  let t2;
  if ($[0] !== details.fileUrl || $[1] !== details.isPdf || $[2] !== details.matches || $[3] !== locale || $[4] !== words) {
    const shown = details.matches.slice(0, INLINE_MATCH_COUNT);
    remaining = details.matches.length - shown.length;
    t1 = "ml-[54px] flex flex-col pb-2 xl:hidden";
    let t3;
    if ($[8] !== details.fileUrl || $[9] !== details.isPdf || $[10] !== locale || $[11] !== words) {
      t3 = match => <li key={match.page}><a href={details.isPdf ? fileUrlOnPage(details.fileUrl, match.page) : details.fileUrl} target="_blank" rel="noopener" className="flex min-h-11 items-center gap-2 text-[13px] text-green-700"><span className="bg-chip text-heading shrink-0 rounded-[5px] px-1.5 py-0.5 text-[11px] font-bold">{pageShortLabel(match.page)[locale]}</span><span className="line-clamp-1"><HighlightedText text={match.snippet} words={words} /></span></a></li>;
      $[8] = details.fileUrl;
      $[9] = details.isPdf;
      $[10] = locale;
      $[11] = words;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    t2 = shown.map(t3);
    $[0] = details.fileUrl;
    $[1] = details.isPdf;
    $[2] = details.matches;
    $[3] = locale;
    $[4] = words;
    $[5] = remaining;
    $[6] = t1;
    $[7] = t2;
  } else {
    remaining = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  let t3;
  if ($[13] !== locale || $[14] !== remaining) {
    t3 = remaining > 0 && <li className="text-conveniat-green text-[13px] font-semibold">{moreMatchesLabel(remaining)[locale]}</li>;
    $[13] = locale;
    $[14] = remaining;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== t1 || $[17] !== t2 || $[18] !== t3) {
    t4 = <ul className={t1}>{t2}{t3}</ul>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
};

/**
 * One result in the list. Below `xl` it is a link to the result. From `xl` on a press chooses it
 * for the preview beside the list instead, which holds the links.
 */
export const SearchResultRow: React.FC<{
  result: SearchResult;
  words: string[];
  locale: Locale;
  isSelected: boolean;
  /** given on a desktop, where the preview shows the chosen result */
  onSelect: ((result: SearchResult) => void) | undefined;
  /** called when the row opens its result */
  onOpen: () => void;
}> = t0 => {
  const $ = _c(28);
  const {
    result,
    words,
    locale,
    isSelected,
    onSelect,
    onOpen
  } = t0;
  const [hasIntent, setHasIntent] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setHasIntent(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const showIntent = t1;
  const t2 = isSelected ? "bg-tint before:bg-conveniat-green before:absolute before:inset-y-0 before:left-0 before:w-[3px]" : "hover:bg-quiet";
  let t3;
  let t4;
  let t5;
  if ($[1] !== hasIntent || $[2] !== isSelected || $[3] !== locale || $[4] !== onOpen || $[5] !== onSelect || $[6] !== result || $[7] !== t2 || $[8] !== words) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const rowClassName = cn("relative flex w-full cursor-pointer items-start gap-4 px-4 py-4 text-left transition-colors sm:px-5", t2);
      let t6;
      if ($[12] !== isSelected || $[13] !== locale || $[14] !== result || $[15] !== words) {
        t6 = <RowBody result={result} words={words} locale={locale} isSelected={isSelected} />;
        $[12] = isSelected;
        $[13] = locale;
        $[14] = result;
        $[15] = words;
        $[16] = t6;
      } else {
        t6 = $[16];
      }
      const body = t6;
      if (onSelect !== undefined) {
        let t7;
        if ($[17] !== onSelect || $[18] !== result) {
          t7 = () => onSelect(result);
          $[17] = onSelect;
          $[18] = result;
          $[19] = t7;
        } else {
          t7 = $[19];
        }
        t5 = <li><button type="button" aria-pressed={isSelected} onClick={t7} className={rowClassName}>{body}</button></li>;
        break bb0;
      }
      t3 = "pb-1";
      t4 = result.type === "document" ? <a href={result.url} target="_blank" rel="noopener" onClick={onOpen} className={cn(rowClassName, hasInlineMatches(result) && "pb-1")}>{body}</a> : <LinkComponent href={result.url} prefetch={hasIntent ? true : "auto"} onMouseEnter={showIntent} onFocus={showIntent} onTouchStart={showIntent} onClick={onOpen} className={rowClassName}>{body}</LinkComponent>;
    }
    $[1] = hasIntent;
    $[2] = isSelected;
    $[3] = locale;
    $[4] = onOpen;
    $[5] = onSelect;
    $[6] = result;
    $[7] = t2;
    $[8] = words;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[20] !== locale || $[21] !== result || $[22] !== words) {
    t6 = <div className="px-4 sm:px-5"><InlineMatches result={result} words={words} locale={locale} /></div>;
    $[20] = locale;
    $[21] = result;
    $[22] = words;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t3 || $[25] !== t4 || $[26] !== t6) {
    t7 = <li className={t3}>{t4}{t6}</li>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
};